import * as Yup from 'yup';

import { REFERRAL_SOURCE_OTHER_ID } from '@/common/constants/referralSources';

export const phoneRegExp =
  /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;

export const isNotFutureDate = (value: string | null | undefined): boolean => {
  if (!value) return true;
  const today = new Date();
  const localToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return value <= localToday;
};

export const CustomerSchema = Yup.object().shape({
  firstName: Yup.string()
    .required('First Name is required')
    .typeError('First Name is required'),
  lastName: Yup.string()
    .required('Last Name is required')
    .typeError('Last Name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required')
    .typeError('Email is required'),
  mobilePhone: Yup.string()
    .matches(phoneRegExp, 'Phone number is not valid')
    .required('Phone number is required')
    .min(10)
    .max(10)
    .typeError('Phone number is required'),
  emergencyEmail: Yup.string().email('Invalid email address').nullable(true),
  preferredContactMethod: Yup.string().nullable(true),
  homePhone: Yup.string()
    .matches(phoneRegExp, 'Phone number not valid')
    .min(10)
    .max(10)
    .nullable(true),
  workPhone: Yup.string()
    .matches(phoneRegExp, 'Phone number not valid')
    .min(10)
    .max(10)
    .nullable(true),
  emergencyPhone: Yup.string()
    .matches(phoneRegExp, 'Phone number not valid')
    .min(10)
    .max(10)
    .nullable(true),
  dob: Yup.string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date of birth')
    .required('Date of birth is required')
    .typeError('Date of birth is required')
    .test('dob-not-future', 'Date of birth cannot be in the future', isNotFutureDate),
  gender: Yup.string()
    .nullable()
    .transform(value => (value === null ? '' : value))
    .required('Gender is required')
});

// Referral-source validators. The referral dropdown only exists on the
// add/edit-customer form and the walk-in modal — NOT on every form that reuses
// CustomerSchema (e.g. EditClientProfile has no dropdown). Keeping these rules
// out of the shared CustomerSchema avoids breaking those other forms; the
// scoped schemas below opt into them.
export const requiredReferralSourceId = Yup.number()
  .typeError('Referral source is required')
  .required('Referral source is required');

// Edit mode: the field is optional (legacy clients may have neither a
// structured source nor free-text), so an empty selection is allowed.
export const optionalReferralSourceId = Yup.number()
  .transform((value, original) => (original === '' ? null : value))
  .nullable();

// "Other" always requires the free text, regardless of create vs edit. Shared by
// the create/edit-customer schemas and PendingTicketNewCustomerSchema.
export const referredBy = Yup.string()
  .nullable()
  .max(255, 'Must be 255 characters or fewer')
  .when('referralSourceId', {
    is: (value: number) => Number(value) === REFERRAL_SOURCE_OTHER_ID,
    then: schema => schema.required('This field is required')
  });

// Create flow (add-customer page + walk-in modal pattern): referral required.
export const AddCustomerSchema = CustomerSchema.concat(
  Yup.object().shape({ referralSourceId: requiredReferralSourceId, referredBy })
);

// Edit flow: referral optional, but "Other" still requires the free text.
export const EditCustomerSchema = CustomerSchema.concat(
  Yup.object().shape({ referralSourceId: optionalReferralSourceId, referredBy })
);
